/**
 * Pure PMD AnimData parsing + spritesheet frame-index math. No Phaser, no network,
 * no DOM — kept separate from SpriteResolver.ts (which needs Phaser/canvas) so the
 * SHEET GEOMETRY is unit-testable in the node environment.
 *
 * PMD overworld sheet layout (verified against the ingested AnimData.xml):
 *   Each species' /generated/pmd/<dex>/AnimData.xml lists <Anim> blocks. The "Walk"
 *   anim carries <FrameWidth>/<FrameHeight> (e.g. 32x40) and a <Durations> list whose
 *   COUNT is the number of animation frames. "Idle" carries its own dims/durations.
 *   The matching PNG (Walk-Anim.png / Idle-Anim.png) is a GRID:
 *     COLUMNS = sheetWidth  / FrameWidth  = animation frames
 *     ROWS    = sheetHeight / FrameHeight = the 8 facing directions (PMD order, row 0 = south)
 *   Phaser numbers spritesheet frames left-to-right, top-to-bottom, so the frame
 *   index for (direction, frame) is: directionRow(direction) * columns + frame.
 *
 * Some anims use <CopyOf>Other</CopyOf> instead of their own dimensions; Walk and
 * Idle on real sheets carry their own, but the parser tolerates a missing/copied
 * block by returning null for that anim so the caller can fall back gracefully.
 */
import { directionRow, type Direction } from './paths.js';

/** Parsed geometry for one PMD anim (Walk or Idle) from AnimData.xml. */
export interface AnimGeometry {
  /** Per-frame width in px (FrameWidth). */
  readonly frameWidth: number;
  /** Per-frame height in px (FrameHeight). */
  readonly frameHeight: number;
  /** Number of animation frames per direction (= the <Durations> count, ≥ 1). */
  readonly frameCount: number;
  /** Per-frame durations in PMD ticks (drives the playback frameRate). */
  readonly durations: readonly number[];
}

/** Both overworld anims a species needs for the populated world. */
export interface PmdAnimData {
  /** The 8-direction walk cycle (null when absent/unparseable). */
  readonly walk: AnimGeometry | null;
  /** The 8-direction idle cycle (null when absent/unparseable). */
  readonly idle: AnimGeometry | null;
}

/**
 * The zero-based row in a PMD sheet for a facing — re-exported from paths so the
 * frame-index math reads in one place. Row 0 = south (PMD_DIRECTION_ORDER).
 */
export { directionRow };

/**
 * Phaser spritesheet frame index for a (direction, frame) cell on a PMD sheet.
 * Frames are numbered row-major, so it is `directionRow(dir) * columns + frame`.
 * `frame` is clamped into `[0, columns)` and `columns` floored at 1, so a bad call
 * never indexes outside the sheet (a stray index would render the wrong cell, not
 * crash — but we keep it honest anyway).
 */
export function frameIndex(dir: Direction, columns: number, frame: number): number {
  const cols = Math.max(1, Math.floor(columns));
  const f = Math.max(0, Math.min(cols - 1, Math.floor(frame)));
  return directionRow(dir) * cols + f;
}

/** The first (standing) frame index for a facing — the default overworld pose. */
export function standingFrameIndex(dir: Direction, columns: number): number {
  return frameIndex(dir, columns, 0);
}

/**
 * How many COLUMNS (animation frames) a sheet has, from its pixel width and the
 * per-frame width. Floors at 1 so a degenerate sheet still yields one usable frame.
 */
export function columnsForSheet(sheetWidth: number, frameWidth: number): number {
  if (!(frameWidth > 0)) return 1;
  return Math.max(1, Math.floor(sheetWidth / frameWidth));
}

/**
 * How many ROWS (facing directions) a sheet has, from its pixel height and the
 * per-frame height. PMD walk/idle sheets are 8 rows; we compute rather than assume
 * so a partial/odd sheet degrades instead of misindexing.
 */
export function rowsForSheet(sheetHeight: number, frameHeight: number): number {
  if (!(frameHeight > 0)) return 1;
  return Math.max(1, Math.floor(sheetHeight / frameHeight));
}

/** Pull the integer text content of `<Tag>123</Tag>` from a block, or null. */
function intTag(block: string, tag: string): number | null {
  const m = new RegExp(`<${tag}>\\s*(-?\\d+)\\s*</${tag}>`).exec(block);
  if (!m) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

/** Count `<Duration>…</Duration>` entries inside an anim block. */
function countDurations(block: string): number[] {
  const out: number[] = [];
  const re = /<Duration>\s*(-?\d+)\s*<\/Duration>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(block)) !== null) {
    const n = Number.parseInt(m[1], 10);
    out.push(Number.isFinite(n) ? n : 1);
  }
  return out;
}

/** Extract the `<Anim>…</Anim>` block whose `<Name>` equals `name` (case-sensitive). */
function findAnimBlock(xml: string, name: string): string | null {
  const re = /<Anim>([\s\S]*?)<\/Anim>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    const block = m[1];
    const nm = /<Name>\s*([^<]+?)\s*<\/Name>/.exec(block);
    if (nm && nm[1] === name) return block;
  }
  return null;
}

/** Parse one anim's geometry by name; null if absent, copied, or missing dims. */
export function parseAnimGeometry(xml: string, name: string): AnimGeometry | null {
  const block = findAnimBlock(xml, name);
  if (!block) return null;
  const frameWidth = intTag(block, 'FrameWidth');
  const frameHeight = intTag(block, 'FrameHeight');
  if (frameWidth == null || frameHeight == null || frameWidth <= 0 || frameHeight <= 0) {
    return null; // e.g. a <CopyOf> block — caller falls back.
  }
  const durations = countDurations(block);
  const frameCount = Math.max(1, durations.length);
  return { frameWidth, frameHeight, frameCount, durations };
}

/**
 * Parse a PMD AnimData.xml string into the Walk + Idle geometry the overworld
 * needs. Pure + total: any miss (empty string, malformed XML, a Walk that is only
 * a <CopyOf>) yields a null for that anim rather than throwing.
 */
export function parseAnimData(xml: string): PmdAnimData {
  if (!xml || typeof xml !== 'string') return { walk: null, idle: null };
  return {
    walk: parseAnimGeometry(xml, 'Walk'),
    idle: parseAnimGeometry(xml, 'Idle'),
  };
}
